import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { DeletedPlansFilters } from '@/components/DeletedPlans/DeletedPlansFilters'
import { DeletedPlansGrid } from '@/components/DeletedPlans/DeletedPlansGrid'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { useDeletedPlansList, useRestorePlan } from '@/hooks/useDeletedPlans'
import { toast } from '@/stores/useToastStore'
import type { ApiPlanSummary } from '@/types'

function toIsoDate(date: Date | undefined): string | undefined {
  if (!date) return undefined
  return date.toISOString().slice(0, 10)
}

export default function DeletedPlans() {
  const navigate = useNavigate()
  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [deletedRange, setDeletedRange] = useState<DateRange | undefined>(
    undefined
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.DELETED_PLANS_LIST,
    20
  )

  // Restore dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [planToRestore, setPlanToRestore] = useState<ApiPlanSummary | null>(
    null
  )

  const deletedAfter = toIsoDate(deletedRange?.from)
  const deletedBefore = toIsoDate(deletedRange?.to)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [searchInput, deletedAfter, deletedBefore, pageSize])

  // Query
  const { data, isLoading, isError } = useDeletedPlansList({
    search: searchInput,
    deletedAfter,
    deletedBefore,
    page,
    pageSize,
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load deleted plans')
    }
  }, [isError])

  const plans = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Restore mutation
  const restoreMutation = useRestorePlan()

  const handleRestoreClick = useCallback((plan: ApiPlanSummary) => {
    setPlanToRestore(plan)
    setRestoreDialogOpen(true)
  }, [])

  const handleConfirmRestore = useCallback(() => {
    if (!planToRestore) return
    restoreMutation.mutate(
      { planId: planToRestore.id },
      {
        onSuccess: () => {
          setRestoreDialogOpen(false)
          setPlanToRestore(null)
        },
      }
    )
  }, [planToRestore, restoreMutation])

  return (
    <>
      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        title="Restore Plan"
        description={`Restore "${planToRestore?.name ?? ''}"? It will reappear in your assortment plans.`}
        confirmText="Restore"
        cancelText="Cancel"
        onConfirm={handleConfirmRestore}
        isLoading={restoreMutation.isPending}
      />

      <div className="flex flex-col gap-6 p-6">
        {/* Page Header */}
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">
            Deleted Plans
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            Search, filter, and restore deleted assortment plans
          </p>
        </div>

        {/* Filter Bar */}
        <DeletedPlansFilters
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          deletedRange={deletedRange}
          onDeletedRangeChange={setDeletedRange}
        />

        {/* Grid */}
        <DeletedPlansGrid
          plans={plans}
          onRestorePlan={handleRestoreClick}
          loading={isLoading}
        />

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </>
  )
}
